import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

import { Observable, Subject, catchError, debounceTime, distinctUntilChanged, filter, map, of, switchMap } from 'rxjs';

import { MpsApiService } from '../../mps/services/mps-api.service';
import { applicationEndpoints } from '../application-endpoints';
import { CourrierSearchParams } from '../interfaces/query-params';
import { LaravelDefaultResponse } from '../interfaces/responses';
import { CourrierSearchResult } from '../models/courrier.model';
import { SearchBridgeService } from './search-bridge.service';

export interface CourrierSearchResponse {
  count: number;
  data: CourrierSearchResult[];
}

const EMPTY_RESPONSE: CourrierSearchResponse = { count: 0, data: [] };

@Injectable({ providedIn: 'root' })
export class CourrierSearchService {
  private readonly apiService = inject(MpsApiService);
  private readonly bridge = inject(SearchBridgeService);

  private readonly searchTrigger$ = new Subject<CourrierSearchParams>();
  private readonly _isLoading = signal(false);

  readonly isLoading: Signal<boolean> = this._isLoading.asReadonly();

  private readonly response = toSignal(
    this.searchTrigger$.pipe(
      switchMap((params) => {
        this._isLoading.set(true);
        return this.apiService
          .get<LaravelDefaultResponse<CourrierSearchResponse>>(
            applicationEndpoints.courrier.search + '?' + this.buildQueryString(params)
          )
          .pipe(
            map((res) => {
              this._isLoading.set(false);
              return res.data;
            }),
            catchError(() => {
              this._isLoading.set(false);
              return of(EMPTY_RESPONSE);
            })
          );
      })
    ),
    { initialValue: EMPTY_RESPONSE }
  );

  readonly searchResults: Signal<CourrierSearchResult[]> = computed(() => this.response().data);
  readonly totalCount: Signal<number> = computed(() => this.response().count);

  search$: Observable<string> = toObservable(this.bridge.query).pipe(
    debounceTime(300),
    distinctUntilChanged(),
    filter((q) => q.length >= 2)
  );

  search(params: CourrierSearchParams): void {
    this.searchTrigger$.next(params);
  }

  private buildQueryString(params: CourrierSearchParams): string {
    let qs = `q=${encodeURIComponent(params.q)}`;
    if (params.page != null) qs += `&page=${params.page}`;
    if (params.pageSize != null) qs += `&pageSize=${params.pageSize}`;
    if (params.type) qs += `&type=${encodeURIComponent(params.type)}`;
    if (params.status) qs += `&status=${encodeURIComponent(params.status)}`;
    if (params.sortField) qs += `&sortField=${encodeURIComponent(params.sortField)}`;
    if (params.sortOrder) qs += `&sortOrder=${params.sortOrder}`;
    return qs;
  }
}
